"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useABTest } from "./useABTest";
import { useUtms } from "./useUtms";
import { FEATURE_FLAGS, HOME_VARIANTS, HomeVariant } from "@/lib/ab-test";
import { trackEvent } from "@/lib/analytics";
import { sendMetaPageView } from "@/lib/meta-capi";
import { sendLead } from "@/lib/lead";
import { TRACKING_EVENTS } from "@/lib/posthog";
import { FormData, isQualified, firstNameOf } from "@/lib/form";
import { appendUtms, type Utms } from "@/lib/utm";
import { preloadCal } from "@/lib/cal";
import { saveLead, loadLead, type FunnelLead } from "@/lib/funnel-store";

// ============================================================================
// Hook d'orchestration du funnel — SPA pilotée par la ROUTE.
//   L'étape courante vient de la route (via la prop `step`). Le lead circule
//   entre les étapes via sessionStorage (@/lib/funnel-store). Centralise
//   tracking (PostHog/Meta/GAds via trackEvent), webhook (full_submission),
//   qualification, préchargement cal.com et la navigation inter-étapes.
//
//   Étapes / routes :
//     lp        → /lp        landing + formulaire
//     vsl       → /vsl       VSL gated (redirige vers /lp sans contexte)
//     vsl-free  → /vsl-free  VSL ungated ; formulaire AVANT la prise de RDV
//     nok       → /nok       profil non qualifié (cohorte complète)
//     ty        → /ty        confirmation post-réservation
// ============================================================================

export type FunnelStep = "lp" | "vsl" | "vsl-free" | "nok" | "ty";

export type Funnel = {
  variant: string;
  home: HomeVariant;
  utms: Utms;
  lead: FunnelLead | null;
  formOpen: boolean;
  calOpen: boolean;
  openForm: (location: string) => void;
  closeForm: () => void;
  completeForm: (data: FormData) => void;
  openBooking: () => void;
  closeCal: () => void;
  onBooked: () => void;
};

export function useFunnel(step: FunnelStep): Funnel {
  const router = useRouter();
  const { variant } = useABTest(FEATURE_FLAGS.HOME_COPY);
  const home = HOME_VARIANTS[variant] ?? HOME_VARIANTS.control;
  const utms = useUtms();

  const [lead, setLead] = useState<FunnelLead | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  // Hydrate le lead + applique le gate d'entrée + tracking d'étape au montage.
  useEffect(() => {
    const stored = loadLead();
    setLead(stored);

    // /vsl est gated : sans contexte (pas passé par le form), retour à /lp.
    if (step === "vsl" && !stored) {
      router.replace(appendUtms("/lp"));
      return;
    }

    trackEvent(TRACKING_EVENTS.PAGE_VIEW, { etape: step });
    // PageView serveur (Meta CAPI), dédupliqué avec le Pixel, sur la landing.
    if (step === "lp") sendMetaPageView();
    // Précharge cal.com dès l'arrivée sur une page vidéo pour un profil qualifié.
    if ((step === "vsl" || step === "vsl-free") && stored?.qualified) preloadCal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const leadProps = (f: FormData, qualified: boolean) => ({
    variant,
    statut: f.statut,
    maturite_ia: f.maturiteIA,
    taille_entreprise: f.tailleEntreprise,
    qualified,
  });

  const openForm = (location: string) => {
    trackEvent(TRACKING_EVENTS.CTA_CLICK, { variant, location });
    setFormOpen(true);
  };
  const closeForm = () => setFormOpen(false);

  // Fin du QCM : qualification + webhook complet, PUIS routage selon l'étape.
  const completeForm = (data: FormData) => {
    const qualified = isQualified(data);
    const next: FunnelLead = {
      nomComplet: data.nomComplet,
      email: data.email,
      phone: data.phone,
      firstName: firstNameOf(data.nomComplet),
      qualified,
      variant,
    };
    saveLead(next);
    setLead(next);

    sendLead("full_submission", data);
    trackEvent(TRACKING_EVENTS.FORM_SUBMISSION, leadProps(data, qualified));
    trackEvent(
      qualified ? TRACKING_EVENTS.LEAD_QUALIFIED : TRACKING_EVENTS.LEAD_DISQUALIFIED,
      leadProps(data, qualified)
    );
    trackEvent(TRACKING_EVENTS.VSL_UNLOCKED, { variant, qualified });
    setFormOpen(false);

    // Depuis la landing → on envoie voir la VSL (gated).
    if (step === "lp") {
      router.push(appendUtms("/vsl"));
      return;
    }
    // Depuis /vsl-free (form demandé AVANT la résa) → RDV direct ou /nok.
    if (qualified) {
      trackEvent(TRACKING_EVENTS.CAL_MODAL_OPENED, { variant });
      preloadCal();
      setCalOpen(true);
    } else {
      trackEvent(TRACKING_EVENTS.BOOKING_UNAVAILABLE, { variant });
      router.push(appendUtms("/nok"));
    }
  };

  // CTA "Réserver" sur /vsl et /vsl-free.
  const openBooking = () => {
    trackEvent(TRACKING_EVENTS.VSL_CTA_CLICK, { variant, qualified: lead?.qualified ?? null });
    // /vsl-free sans contexte : on qualifie via le formulaire AVANT la résa.
    if (!lead) {
      openForm("vsl_booking");
      return;
    }
    if (lead.qualified) {
      trackEvent(TRACKING_EVENTS.CAL_MODAL_OPENED, { variant });
      setCalOpen(true);
    } else {
      trackEvent(TRACKING_EVENTS.BOOKING_UNAVAILABLE, { variant });
      router.push(appendUtms("/nok"));
    }
  };
  const closeCal = () => setCalOpen(false);

  // Réservation confirmée (event cal.com `bookingSuccessful`) → /ty.
  const onBooked = () => {
    const l = lead ?? loadLead();
    if (l) {
      trackEvent(TRACKING_EVENTS.APPLICATION_SUBMITTED, {
        email: l.email,
        variant,
        qualified: true,
      });
    }
    // petit délai pour laisser le beacon analytics partir avant la navigation
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        window.location.href = appendUtms("/ty");
      }, 400);
    }
  };

  return {
    variant,
    home,
    utms,
    lead,
    formOpen,
    calOpen,
    openForm,
    closeForm,
    completeForm,
    openBooking,
    closeCal,
    onBooked,
  };
}
